import { useState } from "react";
import { toast } from "sonner";
import { EVENTS } from "@/data/content";
import { rsvpEvent } from "@/lib/api";
import { useFormattedPhone } from "@/lib/usePhone";
import SmsConsent from "@/components/SmsConsent";

export default function Events() {
  const [openId, setOpenId] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", guests: 1, sms_opt_in: false });
  const phone = useFormattedPhone();
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState({});

  const startRsvp = (id) => {
    setOpenId(id);
    setForm({ name: "", email: "", guests: 1, sms_opt_in: false });
    phone.reset();
  };

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
  const phoneEntered = phone.hasValue;

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please share your name and email.");
      return;
    }
    if (phoneEntered && !phone.isValid) {
      toast.error(phone.error);
      return;
    }
    if (phoneEntered && !form.sms_opt_in) {
      toast.error("Please check the SMS consent box to receive texts.");
      return;
    }
    setSubmitting(true);
    try {
      const data = await rsvpEvent({
        name: form.name,
        email: form.email,
        event_id: openId,
        guests: Number(form.guests) || 1,
        phone: phone.normalized || undefined,
        sms_opt_in: !!form.sms_opt_in,
      });
      setConfirmed((c) => ({ ...c, [openId]: data }));
      toast.success("Your note has been delivered to The House. We'll be in touch soon.");
      setOpenId(null);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="events-page" className="pt-28 md:pt-36 pb-24 md:pb-32">
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="label-eyebrow">Events &amp; Pop-Ups</p>
        <h1 className="font-display text-bone text-5xl md:text-7xl mt-5 leading-[0.95]">
          The House moves.<br />
          <em className="not-italic text-brass">Slowly.</em>
        </h1>
        <p className="mt-6 text-bone/65 max-w-2xl">
          We hold a handful of residencies each year. Private courtyards, pop-ups, and warm farmer&apos;s
          market nights. Seats are limited and almost always taken by House Stewards first. Beat the rush.
        </p>

        <ul className="mt-14 divide-y divide-bone/10 border-y border-bone/10" data-testid="events-list">
          {EVENTS.map((ev) => (
            <li key={ev.id} className="grid md:grid-cols-12 gap-6 py-10 items-start">
              <div className="md:col-span-2">
                <p className="label-eyebrow text-brass">{ev.date}</p>
              </div>
              <div className="md:col-span-6">
                <h3 className="font-display text-bone text-3xl md:text-4xl leading-tight">{ev.title}</h3>
                <p className="font-display italic text-bone/55 text-lg mt-1">{ev.location}</p>
                <p className="mt-4 text-bone/65 max-w-xl">{ev.blurb}</p>
              </div>
              <div className="md:col-span-4 md:text-right">
                {confirmed[ev.id] ? (
                  <span data-testid={`event-rsvp-confirmed-${ev.id}`} className="inline-flex items-center px-5 py-3 border border-lavender/60 text-lavender text-[11px] uppercase tracking-[0.3em]">
                    Reserved — N°{String(confirmed[ev.id].guests).padStart(2, "0")} seats
                  </span>
                ) : (
                  <button
                    data-testid={`event-rsvp-btn-${ev.id}`}
                    onClick={() => startRsvp(ev.id)}
                    className="inline-flex items-center px-6 py-3 border border-bone/30 text-bone text-[11px] uppercase tracking-[0.3em] hover:bg-brass hover:text-obsidian hover:border-brass transition-colors"
                  >
                    Request Seats
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>

        {/* Inline RSVP form */}
        {openId && (
          <div data-testid="rsvp-form-wrapper" className="mt-14 border border-bone/15 p-8 md:p-12 bg-espresso/20">
            <div className="flex items-start justify-between gap-6">
              <div>
                <p className="label-eyebrow text-brass">Request Seats</p>
                <h3 className="font-display text-bone text-3xl mt-3">
                  {EVENTS.find((e) => e.id === openId)?.title}
                </h3>
              </div>
              <button
                data-testid="rsvp-cancel"
                onClick={() => setOpenId(null)}
                className="text-bone/50 hover:text-bone text-[11px] uppercase tracking-[0.28em]"
              >
                Cancel
              </button>
            </div>

            <form onSubmit={onSubmit} data-testid="rsvp-form" className="mt-8 grid md:grid-cols-3 gap-6">
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Name</label>
                <input
                  data-testid="rsvp-input-name"
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-lg"
                  placeholder="Your full name"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Email</label>
                <input
                  data-testid="rsvp-input-email"
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-lg"
                  placeholder="you@somewhere.house"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">Guests</label>
                <input
                  data-testid="rsvp-input-guests"
                  name="guests"
                  type="number"
                  min="1"
                  max="8"
                  value={form.guests}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone font-display text-lg"
                />
              </div>

              <div className="md:col-span-3">
                <label className="block text-[10px] uppercase tracking-[0.3em] text-bone/55 mb-2">
                  Mobile Number <span className="text-bone/35">(optional)</span>
                </label>
                <input
                  data-testid="rsvp-input-phone"
                  name="phone"
                  type="tel"
                  inputMode="tel"
                  value={phone.display}
                  onChange={phone.onChange}
                  className="w-full bg-transparent border-b border-bone/20 focus:border-brass outline-none py-3 text-bone placeholder-bone/30 font-display text-lg"
                  placeholder="1 (555) 555-5555"
                />
                {phoneEntered && !phone.isValid && (
                  <p className="mt-2 text-[11px] text-red-300/85">{phone.error}</p>
                )}
              </div>

              <div className="md:col-span-3">
                <SmsConsent
                  testId="rsvp-sms-consent"
                  checked={form.sms_opt_in}
                  onChange={(v) => setForm((f) => ({ ...f, sms_opt_in: v }))}
                  required={phoneEntered}
                />
              </div>

              <div className="md:col-span-3">
                <button
                  type="submit"
                  data-testid="rsvp-submit"
                  disabled={submitting}
                  className="inline-flex items-center px-8 py-4 bg-brass text-obsidian text-[12px] uppercase tracking-[0.3em] hover:bg-champagne transition-colors disabled:opacity-60"
                >
                  {submitting ? "Holding seats…" : "Confirm Request"}
                </button>
              </div>
            </form>
          </div>
        )}
      </section>
    </div>
  );
}
